import type { ClientPlanCatalogue, ClientPlanCatalogueNode, ClientPlanTopic } from './interfaces';
import { getClientSpeechNeeds } from './clientSpeechNeeds';

// Keep the editor within the assignment contract already supported by TherapyApp.
export const CLIENT_PLAN_MAX_TOPICS = 50;

export type ClientPlanCatalogueRecords = {
    speechNeeds: Array<Record<string, unknown>>;
    conditions: Array<Record<string, unknown>>;
    categories: Array<Record<string, unknown>>;
    goals: Array<Record<string, unknown>>;
};

export type ClientPlanCatalogueScope = {
    providerId: string;
    speechNeedIds?: string[];
};

type CatalogueRecord = Record<string, unknown>;
type CatalogueGroup = { id: string; title: string; sequenceNo: number };
type ParsedGoal = { raw: CatalogueRecord; topic: ClientPlanTopic };

// Ignore malformed scalar fields without converting objects into visible text.
function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

// Read named catalogue documents in the requested language, then English or another translation.
function catalogueTitle(record: CatalogueRecord | undefined, language: string, fallback: string): string {
    if (!record) return fallback;
    const languages = Array.from(new Set([language, language.split('-')[0], 'en', ...Object.keys(record).sort()]));
    for (const key of languages) {
        const translated = record[key];
        if (translated && typeof translated === 'object' && !Array.isArray(translated)) {
            const name = text((translated as CatalogueRecord).name);
            if (name) return name;
        }
    }
    return text(record.name) || fallback;
}

// Sequence values are sometimes stored as strings in the legacy Goals catalogue.
function sequence(record: CatalogueRecord | undefined): number {
    const value = record?.sequenceNo;
    const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

// Resolve condition links consistently for catalogue browsing and assignment validation.
export function clientPlanConditionSpeechNeedIds(
    condition: CatalogueRecord | undefined,
    scope?: ClientPlanCatalogueScope,
    knownSpeechNeedIds?: ReadonlySet<string>,
): string[] {
    const filter = condition?.filter;
    const filterRecord = filter && typeof filter === 'object' && !Array.isArray(filter) ? filter as CatalogueRecord : undefined;
    const relationship = filterRecord && (filterRecord.speechNeeds !== undefined || filterRecord.speechNeed !== undefined)
        ? filterRecord : condition;
    const speechNeedIds = relationship ? getClientSpeechNeeds(relationship) : [];
    if (!scope) return speechNeedIds;

    // Provider-scoped browsing never invents branches for unknown or unassigned relationships.
    if (!condition || !text(scope.providerId)) return [];
    const conditionProviderId = filterRecord?.providerId;
    if (conditionProviderId !== undefined && conditionProviderId !== null &&
        (typeof conditionProviderId !== 'string' || (text(conditionProviderId) && text(conditionProviderId) !== text(scope.providerId)))) return [];
    const allowedIds = scope.speechNeedIds === undefined ? null : new Set(scope.speechNeedIds.map(text).filter(Boolean));
    return speechNeedIds.filter((id) => (!knownSpeechNeedIds || knownSpeechNeedIds.has(id)) && (allowedIds === null || allowedIds.has(id)));
}

// A shared topic is available when at least one real condition belongs to this provider's support areas.
export function isClientPlanGoalAvailableToProvider(
    goal: CatalogueRecord,
    conditions: ReadonlyMap<string, CatalogueRecord>,
    knownSpeechNeedIds: ReadonlySet<string>,
    scope: ClientPlanCatalogueScope,
): boolean {
    if (!parseClientPlanTopic(goal) || !Array.isArray(goal.targetConditions)) return false;
    return goal.targetConditions.some((id) =>
        clientPlanConditionSpeechNeedIds(conditions.get(text(id)), scope, knownSpeechNeedIds).length > 0);
}

// Keep published, openable topics consistent between browsing and assignment validation.
export function parseClientPlanTopic(value: unknown): ClientPlanTopic | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as CatalogueRecord;
    const id = text(raw.id);
    const title = text(raw.title);
    if (!id || !title || !text(raw.rootPage) || raw.draft === true ||
        (raw.type !== undefined && raw.type !== null && raw.type !== '' && raw.type !== 'topic')) return null;
    const iconUrl = text(raw.illustrationUrl);
    const language = text(raw.language);
    return { id, title, completed: false, ...(iconUrl ? { iconUrl } : {}), ...(language ? { language } : {}) };
}

// Flatten a dragged branch in its displayed order, keeping a shared topic only once.
export function collectClientPlanTopics(node: ClientPlanCatalogueNode): ClientPlanTopic[] {
    const topics = new Map<string, ClientPlanTopic>();
    const visit = (entry: ClientPlanCatalogueNode) => {
        if (entry.topic && !topics.has(entry.topic.id)) topics.set(entry.topic.id, entry.topic);
        entry.children.forEach(visit);
    };
    visit(node);
    return Array.from(topics.values());
}

// Use stable path keys so a topic shared by several conditions remains independently browseable.
function nodeId(parts: string[]): string {
    return parts.map(encodeURIComponent).join('/');
}

// Build groups with the exact ordered set of topics that a drag will add to the plan.
function groupNode(group: CatalogueGroup, kind: ClientPlanCatalogueNode['kind'], path: string[], children: ClientPlanCatalogueNode[]): ClientPlanCatalogueNode {
    return {
        id: nodeId([...path, group.id]),
        title: group.title,
        kind,
        children,
        topicIds: Array.from(new Set(children.flatMap((child) => child.topicIds))),
    };
}

// Sort by the authored sequence, using the displayed title and ID to settle missing or tied values.
function compareGroups(a: CatalogueGroup, b: CatalogueGroup): number {
    return a.sequenceNo - b.sequenceNo || a.title.localeCompare(b.title, undefined, { numeric: true }) || a.id.localeCompare(b.id);
}

// Normalise real catalogue links without dropping topics whose older metadata is incomplete.
export function buildClientPlanCatalogue(
    records: ClientPlanCatalogueRecords,
    language = 'en',
    scope?: ClientPlanCatalogueScope,
): Pick<ClientPlanCatalogue, 'nodes' | 'topics'> {
    const speechNeeds = new Map(records.speechNeeds.filter((entry) => text(entry.id)).map((entry) => [text(entry.id), entry]));
    const knownSpeechNeedIds = new Set(speechNeeds.keys());
    const conditions = new Map(records.conditions.filter((entry) => text(entry.id)).map((entry) => [text(entry.id), entry]));
    const categories = new Map(records.categories.filter((entry) => text(entry.id)).map((entry) => [text(entry.id), entry]));
    const uniqueGoals = new Map<string, ParsedGoal>();
    records.goals.forEach((raw) => {
        const topic = parseClientPlanTopic(raw);
        if (topic && !uniqueGoals.has(topic.id)) uniqueGoals.set(topic.id, { raw, topic });
    });
    const goals = Array.from(uniqueGoals.values()).sort((a, b) => sequence(a.raw) - sequence(b.raw) ||
        a.topic.title.localeCompare(b.topic.title, undefined, { numeric: true }) || a.topic.id.localeCompare(b.topic.id));

    // Category IDs are authoritative; old text-only categories can match a single named category.
    const resolveCategory = (raw: CatalogueRecord): CatalogueGroup => {
        const categoryId = text(raw.categoryId);
        const legacyTitle = text(raw.category);
        let category = categoryId ? categories.get(categoryId) : undefined;
        if (!category && !categoryId && legacyTitle) {
            const matches = Array.from(categories.values()).filter((entry) => text(entry.name) === legacyTitle ||
                Object.values(entry).some((value) => value && typeof value === 'object' && !Array.isArray(value) &&
                    text((value as CatalogueRecord).name) === legacyTitle));
            if (matches.length === 1) category = matches[0];
        }
        return {
            id: categoryId ? `id:${categoryId}` : category ? `id:${text(category.id)}` : legacyTitle ? `legacy:${legacyTitle}` : 'unassigned',
            title: catalogueTitle(category, language, legacyTitle || categoryId || 'Other topics'),
            sequenceNo: sequence(category),
        };
    };
    type CategoryBranch = { group: CatalogueGroup; goals: ParsedGoal[] };
    type ConditionBranch = { group: CatalogueGroup; categories: Map<string, CategoryBranch> };
    type NeedBranch = { group: CatalogueGroup; conditions: Map<string, ConditionBranch> };
    const needBranches = new Map<string, NeedBranch>();
    const visibleGoalIds = new Set<string>();

    // Conditions use filter.speechNeeds (or legacy filter.speechNeed), as in TherapyApp's explorer.
    goals.forEach((goal) => {
        const conditionIds = Array.isArray(goal.raw.targetConditions)
            ? Array.from(new Set(goal.raw.targetConditions.map(text).filter(Boolean))) : [];
        (conditionIds.length ? conditionIds : ['']).forEach((conditionId) => {
            const condition = conditions.get(conditionId);
            const speechNeedIds = clientPlanConditionSpeechNeedIds(condition, scope, knownSpeechNeedIds);
            (speechNeedIds.length || scope ? speechNeedIds : ['']).forEach((speechNeedId) => {
                const need = speechNeeds.get(speechNeedId);
                const needKey = speechNeedId ? `id:${speechNeedId}` : 'unassigned';
                if (!needBranches.has(needKey)) needBranches.set(needKey, {
                    group: { id: needKey, title: catalogueTitle(need, language, speechNeedId || 'Unassigned speech need'), sequenceNo: sequence(need) },
                    conditions: new Map(),
                });
                const needBranch = needBranches.get(needKey)!;
                const conditionKey = conditionId ? `id:${conditionId}` : 'unassigned';
                if (!needBranch.conditions.has(conditionKey)) needBranch.conditions.set(conditionKey, {
                    group: { id: conditionKey, title: catalogueTitle(condition, language, conditionId || 'Unassigned condition'), sequenceNo: sequence(condition) },
                    categories: new Map(),
                });
                const conditionBranch = needBranch.conditions.get(conditionKey)!;
                const category = resolveCategory(goal.raw);
                if (!conditionBranch.categories.has(category.id)) conditionBranch.categories.set(category.id, { group: category, goals: [] });
                conditionBranch.categories.get(category.id)!.goals.push(goal);
                visibleGoalIds.add(goal.topic.id);
            });
        });
    });

    // Only show branches containing selectable topics, and retain catalogue ordering at each level.
    const nodes = Array.from(needBranches.values()).sort((a, b) => compareGroups(a.group, b.group)).map((need) =>
        groupNode(need.group, 'speechNeed', [], Array.from(need.conditions.values())
            .sort((a, b) => compareGroups(a.group, b.group)).map((condition) =>
                groupNode(condition.group, 'condition', [need.group.id], Array.from(condition.categories.values())
                    .sort((a, b) => compareGroups(a.group, b.group)).map((category) =>
                        groupNode(category.group, 'category', [need.group.id, condition.group.id], category.goals.map(({ topic }) => ({
                            id: nodeId([need.group.id, condition.group.id, category.group.id, topic.id]),
                            title: topic.title,
                            kind: 'topic',
                            topicIds: [topic.id],
                            children: [],
                            topic,
                        }))))))));
    return { nodes, topics: goals.filter((goal) => visibleGoalIds.has(goal.topic.id)).map((goal) => goal.topic) };
}
